import type { AgreementData } from '@zotlo-shared/elements/common/payment/types';
import { type SiteData } from '#shared/types';
import { PageType } from '@zotlo-shared/types/model';
import type { LocaleObject } from '@nuxtjs/i18n';
import { getFirstPage } from '#shared/utils/session';

export function getNextPage(siteData: SiteData, currentPage: any) {
  const availablePages = siteData?.availablePages || [];
  const pageType = currentPage?.template?.name || getFirstPage(siteData);
  const currentPageIndex = availablePages?.indexOf(pageType);
  const availablePagesCount = availablePages?.length;
  const nextPageIndex = (currentPageIndex + 1) % availablePagesCount;
  let newPage = availablePages[nextPageIndex];

  if (newPage === PageType.REGISTER && siteData?.bypassRegister) {
    newPage = availablePages[(currentPageIndex + 2) % availablePagesCount];
  }

  return newPage;
}

export function useSiteData(data: Record<string, any>) {
  const siteData = (data?.['INITAPP'] || {}) as SiteData;
  const packages = (data?.['PACKAGES']?.packages || []) as Record<string, any>[];
  const agreement = (data?.['PACKAGES']?.agreement || {}) as AgreementData;
  const language = siteData?.lang as LocaleObject['code'];
  const currentPage = siteData?.page;
  const integrations = siteData?.integrations || {};
  const providers = siteData?.providers || {} as SiteData['providers'];
  const applicationLinks = siteData?.applicationLinks || {};
  const socialLoginStatus = siteData?.social || {};
  const availablePages = siteData?.availablePages || [];
  const config = siteData?.config || {};
  const isPreview = !!siteData?.preview;
  const isSandbox = !!siteData?.sandbox;
  const isStoreFlow = !!siteData?.isStoreFlow;
  const nextPage = getNextPage(siteData, currentPage);
  const countryCode = siteData?.countryCode;
  const epinDetails = siteData?.epinDetails || {};
  const isMaintenance = !!siteData?.maintenance;
  const parametersNotProvided = !!siteData?.noparams;
  const restrictedCountries = siteData?.restrictedCountries || [];
  const languages = function () {
    const languagesObj = siteData?.localeParams?.languages || {};
    return Object.entries(languagesObj).map(([key, value]: any[]) => {
      return {
        label: key,
        value: value?.code,
        ...value
      };
    });
  }();
  const countries = function () {
    const countriesObj = siteData?.localeParams?.countries || {};
    return Object.entries(countriesObj).map(([key, value]: any[]) => {
      return {
        label: value?.name,
        value: key,
        ...value
      };
    });
  }();

  return {
    packages,
    agreement,
    siteData,
    currentPage,
    availablePages,
    integrations,
    providers,
    config,
    language,
    isPreview,
    isSandbox,
    isStoreFlow,
    nextPage,
    applicationLinks,
    socialLoginStatus,
    countryCode,
    epinDetails,
    languages,
    countries,
    restrictedCountries,
    isMaintenance,
    parametersNotProvided,
    getNextPage: () => getNextPage(siteData, currentPage)
  }
}
